/**
 * User Management Page
 * Only accessible by Super Admins
 */

import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import useCurrentUser from '../../hooks/useCurrentUser';

function Users() {
    const { getAccessTokenSilently } = useAuth0();
    const { isSuperAdmin, loading: userLoading } = useCurrentUser();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [brands, setBrands] = useState([]);

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [formData, setFormData] = useState({
        email: '',
        name: '',
        role: 'org_viewer',
        orgId: '',
        brandId: ''
    });

    useEffect(() => {
        if (isSuperAdmin) {
            loadData();
        }
    }, [isSuperAdmin]);

    const loadData = async () => {
        try {
            setLoading(true);
            const token = await getAccessTokenSilently();
            const headers = { Authorization: `Bearer ${token}` };

            const [usersRes, brandsRes] = await Promise.all([
                axios.get('/api/admin/users', { headers }),
                axios.get('/api/admin/brands', { headers })
            ]);

            setUsers(usersRes.data.users);
            setBrands(brandsRes.data.brands); // We need all brands for the dropdown
        } catch (err) {
            console.error('Error loading users:', err);
            setError('Failed to load user data. You may not have permission.');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            const token = await getAccessTokenSilently();
            const headers = { Authorization: `Bearer ${token}` };

            if (editingUser) {
                // Update
                await axios.put(`/api/admin/users/${editingUser._id}`, formData, { headers });
            } else {
                // Create
                await axios.post('/api/admin/users', formData, { headers });
            }

            setShowModal(false);
            setEditingUser(null);
            setFormData({ email: '', name: '', role: 'org_viewer', orgId: '', brandId: '' });
            loadData();
        } catch (err) {
            console.error('Error saving user:', err);
            alert('Failed to save user');
        }
    };

    const handleDelete = async (userId) => {
        if (!window.confirm('Are you sure you want to delete this user?')) return;
        try {
            const token = await getAccessTokenSilently();
            await axios.delete(`/api/admin/users/${userId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            loadData();
        } catch (err) {
            console.error('Error deleting user:', err);
            alert('Failed to delete user');
        }
    };

    const openModal = (user = null) => {
        if (user) {
            setEditingUser(user);
            setFormData({
                email: user.email,
                name: user.name,
                role: user.role,
                orgId: user.orgId || '',
                brandId: user.brandId || ''
            });
        } else {
            setEditingUser(null);
            setFormData({ email: '', name: '', role: 'org_viewer', orgId: '', brandId: '' });
        }
        setShowModal(true);
    };

    if (userLoading) return <div className="p-5 text-center">Loading...</div>;

    if (!isSuperAdmin) {
        return (
            <div className="container py-5 text-center">
                <h3>Access Denied</h3>
                <p className="text-muted">Only Super Admins can manage users.</p>
            </div>
        );
    }

    if (loading) return <div className="p-5 text-center">Loading...</div>;

    return (
        <div className="container-fluid py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="mb-0">User Management</h2>
                    <p className="text-muted">Manage system users and their assignments</p>
                </div>
                <button className="btn btn-primary" onClick={() => openModal()}>
                    <i className="bi bi-person-plus me-2"></i>
                    Add User
                </button>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            <div className="card border-0 shadow-sm">
                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th className="ps-4">Name / Email</th>
                                <th>Role</th>
                                <th>Organization</th>
                                <th>Assigned Brand</th>
                                <th className="text-end pe-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(u => (
                                <tr key={u._id}>
                                    <td className="ps-4">
                                        <div className="fw-bold">{u.name}</div>
                                        <div className="text-muted small">{u.email}</div>
                                    </td>
                                    <td>
                                        <span className={`badge ${u.role === 'super_admin' ? 'bg-danger' : 'bg-primary'}`}>
                                            {u.role.replace('_', ' ')}
                                        </span>
                                    </td>
                                    <td>{u.orgId}</td>
                                    <td>
                                        {brands.find(b => b._id === u.brandId)?.name || '-'}
                                    </td>
                                    <td className="text-end pe-4">
                                        <button className="btn btn-sm btn-outline-secondary me-2" onClick={() => openModal(u)}>
                                            Edit
                                        </button>
                                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(u._id)}>
                                            <i className="bi bi-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* User Modal */}
            {showModal && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{editingUser ? 'Edit User' : 'Add User'}</h5>
                                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                            </div>
                            <form onSubmit={handleSave}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">Email</label>
                                        <input
                                            type="email"
                                            className="form-control"
                                            value={formData.email}
                                            onChange={e => setFormData({ ...formData, email: e.target.value })}
                                            required
                                            disabled={!!editingUser} // Email is immutable for ID purposes
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.name}
                                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Role</label>
                                        <select
                                            className="form-select"
                                            value={formData.role}
                                            onChange={e => setFormData({ ...formData, role: e.target.value })}
                                            required
                                        >
                                            <option value="org_viewer">Viewer</option>
                                            <option value="org_manager">Manager</option>
                                            <option value="org_admin">Org Admin</option>
                                            <option value="super_admin">Super Admin</option>
                                        </select>
                                    </div>

                                    {formData.role !== 'super_admin' && (
                                        <>
                                            <div className="mb-3">
                                                <label className="form-label">Organization ID</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={formData.orgId}
                                                    onChange={e => setFormData({ ...formData, orgId: e.target.value })}
                                                    placeholder="e.g. org-topsail-hardware"
                                                    required
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">Assigned Brand (Optional)</label>
                                                <select
                                                    className="form-select"
                                                    value={formData.brandId}
                                                    onChange={e => setFormData({ ...formData, brandId: e.target.value })}
                                                >
                                                    <option value="">-- All Brands in Org --</option>
                                                    {brands.map(b => (
                                                        <option key={b._id} value={b._id}>{b.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                                    <button type="submit" className="btn btn-primary">Save User</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Users;
